import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import AgentCard from '../components/AgentCard'
import Footer from '../components/layout/Footer'
import Topbar from '../components/layout/Topbar'
import ProjectCard from '../components/project/ProjectCard'
import { fetchUserAgents, fetchUserProfile, fetchUserProjects, fetchUserStats } from '../services/api'
import { useAppStore } from '../store/useAppStore'
import type { Agent, Project, User, UserStats } from '../types'

type TabType = 'work' | 'research' | 'agents'

export default function Profile() {
  const { username } = useParams<{ username: string }>()
  const navigate = useNavigate()
  const { user: currentUser } = useAppStore()

  const [user, setUser] = useState<User | null>(null)
  const [stats, setStats] = useState<UserStats | null>(null)
  const [workProjects, setWorkProjects] = useState<Project[]>([])
  const [researchProjects, setResearchProjects] = useState<Project[]>([])
  const [agents, setAgents] = useState<Agent[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<TabType>('work')

  const loadProfile = useCallback(async () => {
    if (!username) {return}
    try {
      setLoading(true)
      setError(null)
      const userData = await fetchUserProfile(username)
      console.log('[Profile] User data:', userData)
      setUser(userData)

      const [statsData, projectsData, agentsData] = await Promise.all([
        fetchUserStats(Number(userData.id)),
        fetchUserProjects(Number(userData.id)),
        fetchUserAgents(Number(userData.id))
      ])

      console.log('[Profile] Stats:', statsData)
      console.log('[Profile] Projects:', projectsData)
      console.log('[Profile] Agents:', agentsData)

      setStats(statsData)

      // Work ve Research projelerini ayır
      const work = projectsData.filter((p: Project) => p.project_type === 'mission')
      const research = projectsData.filter((p: Project) => p.project_type === 'research')
      setWorkProjects(work)
      setResearchProjects(research)

      setAgents(agentsData)
    } catch (err) {
      console.error('Profil yukleme hatasi:', err)
      setError((err as Error).message ?? 'Profil yuklenemedi')
    } finally {
      setLoading(false)
    }
  }, [username])

  useEffect(() => {
    if (username) {
      void loadProfile()
    }
  }, [username, loadProfile])

  if (loading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>        <div style={{ flex: 1, overflow: 'auto' }}>
          <Topbar />
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <p style={{ color: 'var(--text-secondary)' }}>Yukleniyor...</p>
          </div>
          <Footer />
        </div>
      </div>
    )
  }

  if (error || !user || !stats) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>        <div style={{ flex: 1, overflow: 'auto' }}>
          <Topbar />
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: '20px' }}>
            <p style={{ color: 'var(--error-color)', fontSize: '18px' }}>{error || 'Kullanici bulunamadi'}</p>
            <button onClick={() => navigate('/')} style={{ padding: '10px 20px', backgroundColor: 'var(--primary-color)', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>Ana Sayfaya Don</button>
          </div>
          <Footer />
        </div>
      </div>
    )
  }

  const isOwnProfile = currentUser?.username === user.username

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>      <div style={{ flex: 1, overflow: 'auto' }}>
        <Topbar />
        <div style={{ flex: 1, overflow: 'auto', backgroundColor: 'var(--bg-primary)' }}>
          {/* Header */}
          <div style={{ backgroundColor: 'var(--bg-secondary)', borderBottom: '1px solid var(--border-color)', padding: '30px' }}>
            <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ display: 'flex', gap: '20px', flex: 1 }}>
                  <div style={{ width: '100px', height: '100px', borderRadius: '50%', backgroundColor: 'var(--primary-color)' + '30', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '40px', fontWeight: '600', color: 'var(--primary-color)' }}>
                    {user.display_name.charAt(0).toUpperCase()}
                  </div>
                  <div style={{ flex: 1 }}>
                    <h1 style={{ fontSize: '32px', fontWeight: 'bold', color: 'var(--text-primary)', marginBottom: '5px' }}>{user.display_name}</h1>
                    <p style={{ fontSize: '16px', color: 'var(--text-tertiary)', marginBottom: '10px' }}>@{user.username || 'user'}</p>
                    <p style={{ fontSize: '14px', color: 'var(--text-tertiary)', marginBottom: '10px' }}>Member since {user.created_at ? new Date(user.created_at).toLocaleDateString('tr-TR', { year: 'numeric', month: 'long' }) : 'Unknown'}</p>
                    {user.bio ? (
                      <p style={{ fontSize: '15px', color: 'var(--text-secondary)', lineHeight: '1.6', marginTop: '10px' }}>{user.bio}</p>
                    ) : (
                      <p style={{ fontSize: '15px', color: 'var(--text-tertiary)', lineHeight: '1.6', marginTop: '10px', fontStyle: 'italic' }}>No bio available</p>
                    )}
                  </div>
                </div>

                {isOwnProfile && (
                  <button onClick={() => navigate('/dashboard')} style={{ padding: '12px 24px', backgroundColor: 'var(--primary-color)', color: 'white', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '500', cursor: 'pointer' }}>Dashboard</button>
                )}
              </div>

              {/* Stats */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '20px', marginTop: '25px' }}>
                <div><div style={{ fontSize: '24px', fontWeight: 'bold', color: 'var(--primary-color)' }}>{user.level || 1}</div><div style={{ fontSize: '13px', color: 'var(--text-tertiary)' }}>Level</div></div>
                <div><div style={{ fontSize: '24px', fontWeight: 'bold', color: 'var(--text-primary)' }}>{user.xp || user.total_xp || 0}</div><div style={{ fontSize: '13px', color: 'var(--text-tertiary)' }}>XP</div></div>
                <div><div style={{ fontSize: '24px', fontWeight: 'bold', color: '#f59e0b' }}>{user.reputation || 0}</div><div style={{ fontSize: '13px', color: 'var(--text-tertiary)' }}>Itibar</div></div>
                <div><div style={{ fontSize: '24px', fontWeight: 'bold', color: '#10b981' }}>${(user.wallet_balance || 0).toFixed(2)}</div><div style={{ fontSize: '13px', color: 'var(--text-tertiary)' }}>Bakiye</div></div>
                <div><div style={{ fontSize: '24px', fontWeight: 'bold', color: 'var(--text-primary)' }}>{stats.projects_created || 0}</div><div style={{ fontSize: '13px', color: 'var(--text-tertiary)' }}>Proje</div></div>
                <div><div style={{ fontSize: '24px', fontWeight: 'bold', color: 'var(--text-primary)' }}>{stats.agents_created || 0}</div><div style={{ fontSize: '13px', color: 'var(--text-tertiary)' }}>Agent</div></div>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div style={{ backgroundColor: 'var(--bg-secondary)', borderBottom: '1px solid var(--border-color)', padding: '0 30px' }}>
            <div style={{ maxWidth: '1200px', margin: '0 auto', display: 'flex', gap: '30px' }}>
              {(['work', 'research', 'agents'] as TabType[]).map((tab) => {
                const tabLabels: Record<TabType, string> = {
                  work: `Work (${workProjects.length})`,
                  research: `Research (${researchProjects.length})`,
                  agents: `Agents (${agents.length})`
                }

                return (
                  <button key={tab} onClick={() => setActiveTab(tab)} style={{ padding: '15px 0', backgroundColor: 'transparent', border: 'none', borderBottom: activeTab === tab ? `2px solid var(--primary-color)` : '2px solid transparent', color: activeTab === tab ? 'var(--primary-color)' : 'var(--text-secondary)', fontSize: '14px', fontWeight: '500', cursor: 'pointer', transition: 'all 0.2s' }}>
                    {tabLabels[tab]}
                  </button>
                )
              })}
            </div>
          </div>

          {/* Content */}
          <div style={{ padding: '30px' }}>
            <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
              {activeTab === 'work' && (
                workProjects.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '60px 20px', backgroundColor: 'var(--bg-secondary)', borderRadius: '12px' }}>
                    <p style={{ fontSize: '16px', color: 'var(--text-secondary)' }}>Henuz work projesi yok</p>
                  </div>
                ) : (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '20px' }}>
                    {workProjects.map(project => <ProjectCard key={project.id} project={project} />)}
                  </div>
                )
              )}

              {activeTab === 'research' && (
                researchProjects.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '60px 20px', backgroundColor: 'var(--bg-secondary)', borderRadius: '12px' }}>
                    <p style={{ fontSize: '16px', color: 'var(--text-secondary)' }}>Henuz research projesi yok</p>
                  </div>
                ) : (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '20px' }}>
                    {researchProjects.map(project => <ProjectCard key={project.id} project={project} />)}
                  </div>
                )
              )}

              {activeTab === 'agents' && (
                agents.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '60px 20px', backgroundColor: 'var(--bg-secondary)', borderRadius: '12px' }}>
                    <p style={{ fontSize: '16px', color: 'var(--text-secondary)' }}>Henuz agent yok</p>
                  </div>
                ) : (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '20px' }}>
                    {agents.map(agent => <AgentCard key={agent.id} agent={agent} onClick={(id) => navigate(`/agent/${id}`)} />)}
                  </div>
                )
              )}


            </div>
          </div>
        </div>
        <Footer />
      </div>
    </div>
  )
}
